import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import {
  V2BookConnectionState,
  type V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  useReactivateWorkspaceBookConnection,
  getRetrieveAllWorkspaceBookConnectionsQueryKey,
  getRetrieveWorkspaceBookConnectionsQueryKey,
} from '@/generated/hooks/bdk/v2/services/library';
import { captureException } from '@/modules/sentry';

/** True when the connection was scaled to zero after a period of inactivity. */
export const isConnectionInactive = (
  connection?: V2BookConnectionDescriptor,
): boolean =>
  connection?.status?.state ===
  V2BookConnectionState.BOOK_CONNECTION_STATE_INACTIVE;

/**
 * Hook exposing the "reactivate" action for connections that BDK scaled to
 * zero after a period of inactivity (`BOOK_CONNECTION_STATE_INACTIVE`).
 *
 * Reactivation moves the connection back out of INACTIVE so the controller
 * scales its pod up again. The connection returns to PENDING while the rollout
 * happens, which the existing 5s connection polling picks up on its own.
 *
 * @param organizationId - Current organization ID
 * @param workspaceId - Current workspace ID
 * @returns `reactivateConnection` action and its pending flag
 */
export const useReactivateConnection = (
  organizationId: string,
  workspaceId: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('integrations');

  const { mutateAsync: reactivateWorkspaceBookConnection, isPending } =
    useReactivateWorkspaceBookConnection();

  // Which connection ids are mid-reactivation. The mutation's own `isPending` is
  // shared across every row, so using it to disable buttons would freeze all
  // inactive rows while any one of them reactivates. Tracking ids lets each row
  // disable only itself.
  const [reactivatingIds, setReactivatingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  /**
   * Reactivates every inactive connection in the given group.
   *
   * A row can represent a draft/published pair sharing one name, so all of its
   * inactive members are reactivated together — reactivating only the first
   * would leave the other environment scaled down.
   *
   * @param connections - Connections belonging to the row the user acted on
   * @returns True when nothing needed reactivating or every request succeeded
   */
  const reactivateConnection = useCallback(
    async (connections: V2BookConnectionDescriptor[]): Promise<boolean> => {
      const inactive = connections.filter(
        conn => isConnectionInactive(conn) && !!conn.connection_id,
      );
      if (inactive.length === 0) return true;

      const toastId = toast.loading(
        t('integrations.connections.reactivateLoading'),
      );

      const ids = inactive.map(conn => conn.connection_id!);
      setReactivatingIds(prev => new Set([...prev, ...ids]));

      try {
        await Promise.all(
          inactive.map(conn =>
            reactivateWorkspaceBookConnection({
              organizationId,
              workspaceId,
              connectionId: conn.connection_id!,
              data: {},
            }),
          ),
        );

        queryClient.invalidateQueries({
          queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
            organizationId,
            workspaceId,
          ),
        });
        // Book-scoped list backing the integration details page. The group can
        // only ever span one book, so the first entry identifies them all.
        queryClient.invalidateQueries({
          queryKey: getRetrieveWorkspaceBookConnectionsQueryKey(
            organizationId,
            workspaceId,
            inactive[0].book_name,
            inactive[0].book_version,
          ),
        });

        toast.dismiss(toastId);
        toast.success(t('integrations.connections.reactivateSuccess'));
        return true;
      } catch (error) {
        toast.dismiss(toastId);
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: {
              module: 'integrations',
              action: 'reactivateConnection',
            },
          },
        );
        toast.error(t('integrations.connections.reactivateError'));
        return false;
      } finally {
        setReactivatingIds(prev => {
          const next = new Set(prev);
          ids.forEach(id => next.delete(id));
          return next;
        });
      }
    },
    [
      organizationId,
      workspaceId,
      queryClient,
      reactivateWorkspaceBookConnection,
      t,
    ],
  );

  /**
   * Whether any of the given connections is currently being reactivated. Pass a
   * row's own connections so it disables only itself.
   *
   * @param connections - Connections belonging to the row being rendered
   * @returns True when at least one of them has a reactivation in flight
   */
  const isReactivatingAny = useCallback(
    (connections: V2BookConnectionDescriptor[]) =>
      connections.some(
        conn => !!conn.connection_id && reactivatingIds.has(conn.connection_id),
      ),
    [reactivatingIds],
  );

  return {
    reactivateConnection,
    /** True while any reactivation is in flight, regardless of which row. */
    isReactivating: isPending,
    isReactivatingAny,
  };
};
